import React from 'react';
import { H2 } from '@hobs/components/Headings';
import { createLoadingSelector } from '@hobs/utils/loading';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next/src/index';
import { Formik, Form, Field, FieldArray } from 'formik';
import PropTypes from 'prop-types';
import Input from '@hobs/components/Input';
import Checkbox from '@hobs/components/Checkbox';
import ObjectPath from 'object-path';
import Tooltip from '@hobs/components/Tooltip';
import Toast from '@hobs/components/Toast';
import * as Yup from 'yup';
import { DATE_FORMAT, MAX_INT32 } from '@hobs/utils/constants';
import { format } from '@hobs/utils/formatWithTimeZone';
import classnames from 'classnames';
import { RaisedButton } from '@hobs/components/Buttons';
import Quantity from '@hobs/components/Quantity';
import {
  getCorInformation,
  clearCorData,
  updateCorInformation
} from '../../../actions/cor';
import Styles from './SorCor.module.scss';

class SorCor extends React.PureComponent {
  corDataCopy = {};

  state = {
    corData: {},
    total: null
  };

  constructor(props) {
    super(props);

    this.locationQueryTimeout = null;
    const { t } = this.props;

    const VALIDATION = {
      quantity: Yup.number()
        .min(1, t('Number of PAX must be greater than 0'))
        .max(MAX_INT32, t('Number of PAX is too large'))
        .typeError(t('Invalid number'))
        .transform((value, originalValue) => {
          return originalValue === 0 || originalValue === '' ? null : value;
        })
        .nullable()
        .default(null),
      comments: Yup.string()
        .nullable()
        .max(256, t('Comment cannot be longer than 256 symbols')),
      number: Yup.number()
        .typeError(t('Invalid number'))
        .min(0, t('Number must be positive'))
        .max(MAX_INT32, t('Number is too large'))
        .transform((value, originalValue) => {
          return originalValue === '' ? 0 : value;
        })
        .default(0)
    };
    this.corItemSchema = Yup.object().shape({
      pax: Yup.array().of(
        Yup.object().shape({
          quantity: VALIDATION.quantity,
          comment: VALIDATION.comments,
          units: Yup.array().of(
            Yup.object().shape({
              quantity: VALIDATION.quantity,
              comment: VALIDATION.comments,
              units: Yup.array().of(
                Yup.object().shape({
                  quantity: VALIDATION.quantity,
                  comment: VALIDATION.comments
                })
              )
            })
          )
        })
      ),
      equipment: Yup.array().of(
        Yup.object().shape({
          vehicles: VALIDATION.number,
          tracks: VALIDATION.number,
          containers: VALIDATION.number,
          ships: VALIDATION.number,
          aircraft: VALIDATION.number,
          comments: VALIDATION.comments
        })
      )
    });
  }

  async componentDidMount() {
    await this.props.getCorInformation().then(response => {
      if (response && response.status === 200) {
        this.corDataCopy = {
          ...this.corDataCopy,
          ...response.payload
        };
      }
    });

    await this.setState({
      corData: {
        ...this.corDataCopy
      }
    });

    await this.setCorData();
  }

  setCorData = async () => {
    const { corData } = this.state;
    await this.setSubUnitLevelQuantity(corData.pax, 'pax');
    await this.setUnitLevelQuantity(corData.pax, 'pax');
    await this.getPaxTotalQuantity(corData.pax, 'pax');
    await this.getEquipmentQuantity(corData.equipment);
  };

  componentWillUnmount() {
    this.props.clearCorData();
  }

  canRenderForm = () => {
    return Object.keys(this.state.corData).length > 0;
  };

  handleSubmit = (values, { setSubmitting, setErrors }) => {
    const { t } = this.props;
    const body = this.state.corData;
    this.props.updateCorInformation(body).then(response => {
      if (response.success) {
        Toast.success(t('COR information updated successfully'));
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning(t('Cannot update COR information'));
      }

      setSubmitting(false);
    });
  };

  getPaxTotalQuantity = (units = [], key) => {
    let total = 0;
    units.forEach((unit, index) => {
      total += Number(unit.quantity);
      unit.units &&
        this.getPaxTotalQuantity(unit.units, `${key}.${index}.quantity`);
    });

    this.setState({
      total: total
    });
  };

  getEquipmentQuantity = (units = []) => {
    let equipment = {
      vehicles: 0,
      tracks: 0,
      containers: 0,
      ships: 0,
      aircraft: 0
    };

    const value = units.reduce((total, unit) => {
      return (equipment = {
        ...equipment,
        vehicles: Number(equipment.vehicles) + Number(unit.vehicles),
        tracks: Number(equipment.tracks) + Number(unit.tracks),
        containers: Number(equipment.containers) + Number(unit.containers),
        ships: Number(equipment.ships) + Number(unit.ships),
        aircraft: Number(equipment.aircraft) + Number(unit.aircraft)
      });
    }, {});

    this.setState({
      ...this.state,
      ...value
    });
  };

  calculateEquipment = ({ target }) => {
    ObjectPath.set(this.corDataCopy, target.name, Number(target.value));
    this.setState({
      corData: {
        ...this.corDataCopy
      }
    });
    this.getEquipmentQuantity(this.state.corData.equipment);
  };

  renderEquipmentFields = (units = [], key) => {
    const { t, disabled } = this.props;
    return units.map((unit, index) => {
      return (
        <div className={Styles['table']} key={`equipment${index}`}>
          <div className={Styles['row']}>
            <div
              className={classnames(Styles['unit-group'], Styles['unit-name'])}
            >
              <div className={Styles['unit-name']}>
                {unit.unitName}
                <span className={Styles['date']}>
                  {unit.likelyFrom && format(unit.likelyFrom, DATE_FORMAT)}
                  {' - '}
                  {unit.likelyTo && format(unit.likelyTo, DATE_FORMAT)}
                </span>
              </div>
            </div>
            <div className={Styles['unit-group']}>
              <div className={Styles['equipment-input-wrapper']}>
                <Field
                  name={`${key}.${index}.vehicles`}
                  component={Input}
                  disabled={disabled}
                  onKeyUp={event => {
                    this.calculateEquipment(event);
                  }}
                  type="number"
                />
              </div>
              <div className={Styles['equipment-input-wrapper']}>
                <Field
                  name={`${key}.${index}.tracks`}
                  component={Input}
                  disabled={disabled}
                  onKeyUp={event => {
                    this.calculateEquipment(event);
                  }}
                  type="number"
                />
              </div>
              <div className={Styles['equipment-input-wrapper']}>
                <Field
                  name={`${key}.${index}.containers`}
                  component={Input}
                  disabled={disabled}
                  onKeyUp={event => {
                    this.calculateEquipment(event);
                  }}
                  type="number"
                />
              </div>
              <div className={Styles['equipment-input-wrapper']}>
                <Field
                  name={`${key}.${index}.ships`}
                  component={Input}
                  disabled={disabled}
                  onKeyUp={event => {
                    this.calculateEquipment(event);
                  }}
                  type="number"
                />
              </div>
              <div className={Styles['equipment-input-wrapper']}>
                <Field
                  name={`${key}.${index}.aircraft`}
                  component={Input}
                  disabled={disabled}
                  onKeyUp={event => {
                    this.calculateEquipment(event);
                  }}
                  type="number"
                />
              </div>
              <div className={Styles['equipment-input-wrapper']}>
                <Field
                  name={`${key}.${index}.comments`}
                  component={Input}
                  placeholder={t('Comments')}
                  disabled={disabled}
                  onKeyUp={this.handleChange}
                />
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  calculatePaxFieldValue = (component = [], key, parentId) => {
    const value = component.reduce((total, unit) => {
      if (unit.parentUnitId === parentId) {
        return Number(total) + Number(unit.quantity);
      }
    }, null);

    if (value !== null) {
      ObjectPath.set(this.corDataCopy, key, value > 0 ? Number(value) : '');
      this.setState({
        corData: {
          ...this.corDataCopy
        }
      });
    }
  };

  setSubUnitLevelQuantity = (units = [], key) => {
    units.reduce((total, unit, index) => {
      if (unit.unitTypeId === 2 && unit.units.length > 0) {
        this.calculatePaxFieldValue(
          unit.units,
          `${key}.${index}.quantity`,
          unit.unitId
        );
      }
      unit.units.length > 0 &&
        this.setSubUnitLevelQuantity(unit.units, `${key}.${index}.units`);
    }, []);
  };

  setUnitLevelQuantity = (units = [], key) => {
    let total = null;
    let fieldName = null;
    units.forEach((unit, index) => {
      if (unit.unitTypeId === 2 && unit.unitTypeId !== 3) {
        total += Number(unit.quantity);
        fieldName = key;
      }
      unit.units &&
        this.setUnitLevelQuantity(unit.units, `${key}.${index}.quantity`);
    });

    if (total !== null && fieldName !== null && fieldName !== 'pax') {
      ObjectPath.set(
        this.corDataCopy,
        fieldName,
        total > 0 ? Number(total) : ''
      );

      this.setState({
        corData: {
          ...this.corDataCopy
        }
      });
    }
  };

  handleQuantityChange = ({ target }) => {
    ObjectPath.set(
      this.corDataCopy,
      target.name,
      parseFloat(target.value) ? parseFloat(target.value) : null
    );

    this.setState({
      corData: {
        ...this.corDataCopy
      }
    });

    this.setSubUnitLevelQuantity(this.state.corData.pax, 'pax');
    this.setUnitLevelQuantity(this.state.corData.pax, 'pax');
    this.getPaxTotalQuantity(this.state.corData.pax, 'pax');
  };

  handleCheckboxChange = ({ target }) => {
    ObjectPath.set(this.corDataCopy, target.name, target.checked);
    this.setState({
      corData: {
        ...this.corDataCopy
      }
    });
  };

  handleChange = ({ target }) => {
    ObjectPath.set(this.corDataCopy, target.name, target.value);
    this.setState({
      corData: {
        ...this.corDataCopy
      }
    });
  };

  paxFieldsComponent = (unit, key, index, errors, setFieldValue) => {
    const { t, disabled } = this.props;
    const isFormFieldsAvailable =
      !(unit.units && unit.units.length > 0) && !disabled;
    return (
      <div className={Styles['row']}>
        <div className={classnames(Styles['unit-group'], Styles['unit-name'])}>
          <div
            className={
              key === 'pax'
                ? Styles['unit-name']
                : key.match(/unit/g).length <= 1
                ? classnames(Styles['unit-name'], Styles['sub-unit'])
                : classnames(Styles['unit-name'], Styles['sub-component'])
            }
          >
            {unit.unitName}
          </div>
          <span className={Styles['date']}>
            {unit.likelyFrom && format(unit.likelyFrom, DATE_FORMAT)}
            {' - '}
            {unit.likelyTo && format(unit.likelyTo, DATE_FORMAT)}
          </span>
        </div>
        <div className={Styles['unit-group']}>
          <Field
            name={`${key}.${index}.quantity`}
            render={({ field }) => (
              <div className={Styles['input-wrapper']}>
                <Input
                  {...field}
                  onChange={event => {
                    this.handleQuantityChange(event);
                    setFieldValue(event.target.name, event.target.value);
                  }}
                  error={
                    Number(field.value) < 1 &&
                    field.value !== null &&
                    field.value !== '' &&
                    isFormFieldsAvailable
                      ? t('Number of PAX must be greater than 0')
                      : field.value > MAX_INT32
                      ? t('Number of PAX is too large')
                      : null
                  }
                  value={ObjectPath.get(
                    this.state.corData,
                    `${key}.${index}.quantity`
                  )}
                  type="number"
                  disabled={!isFormFieldsAvailable}
                />
              </div>
            )}
          />

          {isFormFieldsAvailable && (
            <Field
              name={`${key}.${index}.accomodation`}
              component={Checkbox}
              onClick={this.handleCheckboxChange}
            />
          )}
          {isFormFieldsAvailable && (
            <Field
              name={`${key}.${index}.meals`}
              component={Checkbox}
              onClick={this.handleCheckboxChange}
            />
          )}
          {isFormFieldsAvailable && (
            <Field
              name={`${key}.${index}.comment`}
              render={({ field }) => (
                <div className={Styles['input-wrapper']}>
                  <Input
                    {...field}
                    name={`${key}.${index}.comment`}
                    placeholder={t('Enter location and other details')}
                    error={
                      field.value !== null
                        ? field.value.length > 256
                          ? t('Comment cannot be longer than 256 symbols')
                          : null
                        : null
                    }
                    onChange={this.handleChange}
                    value={ObjectPath.get(
                      this.state.corData,
                      `${key}.${index}.comment`
                    )}
                  />
                </div>
              )}
            />
          )}
        </div>
      </div>
    );
  };

  renderPaxFields = (units = [], key, errors, setFieldValue) => {
    return units.map((unit, index) => {
      if (key === 'pax') {
        return (
          <div className={Styles['table']} key={`pax${index}`}>
            {this.paxFieldsComponent(unit, key, index, errors, setFieldValue)}
            {unit.units &&
              this.renderPaxFields(
                unit.units,
                `${key}.${index}.units`,
                errors,
                setFieldValue
              )}
          </div>
        );
      } else {
        return (
          <React.Fragment key={`sub-unit${key}[${index}]`}>
            {this.paxFieldsComponent(unit, key, index, errors, setFieldValue)}
            {unit.units &&
              this.renderPaxFields(
                unit.units,
                `${key}.${index}.units`,
                errors,
                setFieldValue
              )}
          </React.Fragment>
        );
      }
    });
  };

  sortUnits(units) {
    if (!Array.isArray(units)) {
      return [];
    }

    const sortedUnits = units.sort((a, b) =>
      a.unitName.toLowerCase() > b.unitName.toLowerCase()
        ? 1
        : a.unitName.toLowerCase() < b.unitName.toLowerCase()
        ? -1
        : 0
    );

    return sortedUnits.map(unit => {
      return {
        ...unit,
        units: this.sortUnits(unit.units)
      };
    });
  }

  render() {
    const { t, disabled } = this.props;
    const { corData } = this.state;
    return (
      <>
        {!this.props.isLoading && Object.keys(corData).length > 0 && (
          <Formik
            initialValues={corData}
            onSubmit={this.handleSubmit}
            validationSchema={this.corItemSchema}
            validateOnBlur={false}
            validateOnChange={true}
            enableReinitialize={true}
            render={({ errors, isSubmitting, setFieldValue }) => {
              return (
                <Form>
                  <div className={Styles['wrapper']}>
                    <div className={Styles['top-wrapper']}>
                      <H2>{t('PAX')}</H2>
                    </div>
                    <div
                      className={classnames(
                        Styles['table-wrapper'],
                        Styles['pax']
                      )}
                    >
                      <div
                        className={classnames(Styles['table'], Styles['title'])}
                      >
                        <div className={Styles['row']}>
                          <div
                            className={classnames(
                              Styles['unit-group'],
                              Styles['unit-name']
                            )}
                          >
                            <div className={Styles['unit-name']}>
                              {t('Element')}
                            </div>
                          </div>
                          <div className={Styles['unit-group']}>
                            <div className={Styles['title']}>
                              {t('Number of PAX')}
                            </div>
                            <div className={Styles['title']}>
                              {t('Accommodation')}{' '}
                              <Tooltip
                                position="top"
                                tooltip={t(
                                  `Please tick off, if accommodation will be
                                  requested partially or in full for this unit`
                                )}
                              />
                            </div>
                            <div className={Styles['title']}>
                              {t('Meals')}{' '}
                              <Tooltip
                                position="top"
                                tooltip={t(
                                  `Please tick off, if meals will be requested
                                  partially or in full for this unit`
                                )}
                              />
                            </div>
                            <div className={Styles['title']}>
                              {t('Comment')}{' '}
                              <Tooltip
                                position="top"
                                tooltip={t(
                                  `Please enter your comments and further
                                  specifications related to desired location,
                                  accommodation and meals`
                                )}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <FieldArray
                        name="pax"
                        render={() =>
                          this.renderPaxFields(
                            this.sortUnits(corData.pax),
                            'pax',
                            errors,
                            setFieldValue
                          )
                        }
                      />

                      <div
                        className={classnames(Styles['table'], Styles['total'])}
                      >
                        <div className={Styles['row']}>
                          <div className={Styles['unit-group']}>
                            <div className={Styles['unit-name']}>
                              {t('Total')}
                            </div>
                            <div>
                              <Quantity amount={this.state.total} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={Styles['wrapper']}>
                    <div className={Styles['top-wrapper']}>
                      <H2>{t('Equipment')}</H2>
                    </div>

                    {!this.props.isLoading && corData.equipment ? (
                      <div
                        className={classnames(
                          Styles['table-wrapper'],
                          Styles['equipment']
                        )}
                      >
                        <div
                          className={classnames(
                            Styles['table'],
                            Styles['title']
                          )}
                        >
                          <div className={Styles['row']}>
                            <div
                              className={classnames(
                                Styles['unit-group'],
                                Styles['unit-name']
                              )}
                            >
                              <div className={Styles['unit-name']}>
                                {t('Element')}
                              </div>
                            </div>
                            <div className={Styles['unit-group']}>
                              <div className={Styles['title']}>
                                {t('Vehicles')}
                              </div>
                              <div className={Styles['title']}>
                                {t('Tracks')}
                              </div>
                              <div className={Styles['title']}>
                                {t('Containers')}
                              </div>
                              <div className={Styles['title']}>
                                {t('Ships')}
                              </div>
                              <div className={Styles['title']}>
                                {t('Aircraft')}
                              </div>
                              <div className={Styles['title']}>
                                {t('Comment')}
                              </div>
                            </div>
                          </div>
                        </div>

                        <FieldArray
                          name="equipment"
                          render={() =>
                            this.renderEquipmentFields(
                              this.sortUnits(corData.equipment),
                              'equipment',
                              errors
                            )
                          }
                        />

                        <div
                          className={classnames(
                            Styles['table'],
                            Styles['total']
                          )}
                        >
                          <div className={Styles['row']}>
                            <div
                              className={classnames(
                                Styles['unit-group'],
                                Styles['unit-name']
                              )}
                            >
                              <div className={Styles['unit-name']}>
                                {t('Total')}
                              </div>
                            </div>
                            <div className={Styles['unit-group']}>
                              <div className={Styles['total']}>
                                <Quantity amount={this.state.vehicles} />
                              </div>
                              <div className={Styles['total']}>
                                <Quantity amount={this.state.tracks} />
                              </div>
                              <div className={Styles['total']}>
                                <Quantity amount={this.state.containers} />
                              </div>
                              <div className={Styles['total']}>
                                <Quantity amount={this.state.ships} />
                              </div>
                              <div className={Styles['total']}>
                                <Quantity amount={this.state.aircraft} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {!disabled && (
                    <div className={Styles['wrapper']}>
                      <div className={Styles['bottom-wrapper']}>
                        <RaisedButton type="submit" disabled={isSubmitting}>
                          {t('Save')}
                        </RaisedButton>
                      </div>
                    </div>
                  )}
                </Form>
              );
            }}
          />
        )}
      </>
    );
  }
}

SorCor.propTypes = {
  sorId: PropTypes.string,
  getCorInformation: PropTypes.func,
  updateCorInformation: PropTypes.func,
  corData: PropTypes.object,
  clearCorData: PropTypes.func,
  t: PropTypes.func,
  query: PropTypes.array,
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool
};

const corDataLoading = createLoadingSelector([
  'StatementOfRequirements/GetCor'
]);

const mapStateToProps = state => ({
  corData: state.cor.cor,
  isLoading: corDataLoading(state)
});

const mapDispatchToProps = {
  getCorInformation,
  clearCorData,
  updateCorInformation
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(SorCor));
